import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (authError) throw authError;

      if (plan) {
        // Start checkout for the selected plan
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ tier: plan }),
          }
        );
        const json = await res.json();
        if (json.url) { window.location.href = json.url; return; }
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const GOLD = '#c8a04e';
  const W = (a) => `rgba(255,255,255,${a})`;
  const GA = (a) => `rgba(200,160,78,${a})`;

  const STEPS = [
    {
      n: '01',
      title: 'Choose Your Plan',
      desc: 'Pick the subscription that fits your schedule — 7 days, 30 days, or lifetime access.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
      ),
    },
    {
      n: '02',
      title: 'Download & Install',
      desc: 'Grab the installer, run it, enter your activation key, and you\'re in.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 17l4 4 4-4"/><path d="M12 12v9"/><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"/>
        </svg>
      ),
    },
    {
      n: '03',
      title: 'Load & Dominate',
      desc: 'Select your weapon profile, launch the game, and let Athena handle the recoil.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', overflow: 'hidden' }}>

      {/* ── LEFT: Steps ── */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 64px' }}>

        {/* Grid lines */}
        <style>{`
          @keyframes loginDrop {
            0%   { top: -20%; }
            100% { top: 110%; }
          }
        `}</style>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          {[
            { left: '10%', delay: '0s'   },
            { left: '25%', delay: '2.2s' },
            { left: '42%', delay: '4.5s' },
            { left: '58%', delay: '0.8s' },
            { left: '75%', delay: '3.1s' },
            { left: '90%', delay: '1.6s' },
          ].map(({ left, delay }, i) => (
            <div key={i} style={{ position: 'absolute', width: 1, height: '100%', top: 0, left, background: 'rgba(255,255,255,0.055)', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', width: '100%', height: '15vh', left: 0, top: '-20%', background: 'linear-gradient(to bottom, rgba(200,160,78,0) 0%, rgba(200,160,78,0.65) 75%, rgba(200,160,78,0.85) 100%)', animation: `loginDrop 7s ${delay} infinite cubic-bezier(0.4,0.26,0,0.97)`, animationFillMode: 'backwards' }} />
            </div>
          ))}
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: `${Math.round((i/5)*100)}%`, height: 1, background: 'rgba(255,255,255,0.04)' }} />
          ))}
        </div>

        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.04,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }} />

        {/* Right edge fade into divider */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to right, transparent, #000)', zIndex: 2, pointerEvents: 'none' }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 3 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 12px' }}>Get Started</p>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 48px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Up and running<br/>in 3 steps.</h2>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {STEPS.map(({ n, title, desc, icon }, idx) => (
              <div key={n}>
                {/* Step row */}
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: GA(0.08), border: `1px solid ${GA(0.18)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: GA(0.5), letterSpacing: '0.1em', marginBottom: 5 }}>STEP {n}</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 5 }}>{title}</div>
                    <div style={{ fontSize: 13, color: W(0.38), lineHeight: 1.6 }}>{desc}</div>
                  </div>
                </div>
                {/* Connector */}
                {idx < STEPS.length - 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 44, height: 48 }}>
                    <div style={{ width: 1, height: '100%', background: GA(0.25) }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DIVIDER ── */}
      <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

      {/* ── RIGHT: Login Form ── */}
      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 56px', overflow: 'hidden', position: 'relative' }}>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          style={{ width: '100%', maxWidth: 400 }}
        >
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Welcome Back</h1>
            <p style={{ fontSize: 13.5, color: W(0.35), margin: 0 }}>Sign in to your Athena account</p>
          </div>

          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              style={{ marginBottom: 20, padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, fontSize: 12.5, color: 'rgba(239,68,68,0.9)', textAlign: 'center' }}>
              {error}
            </motion.div>
          )}

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: W(0.3), letterSpacing: '0.1em', marginBottom: 7, textTransform: 'uppercase' }}>Email Address</label>
              <input
                type="email" required placeholder="name@example.com"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '11px 16px', color: '#fff', fontSize: 13.5, outline: 'none', transition: 'border-color .15s' }}
                onFocus={e => e.target.style.borderColor = GA(0.4)}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: W(0.3), letterSpacing: '0.1em', textTransform: 'uppercase' }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: 10, fontWeight: 700, color: GOLD, textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase', transition: 'opacity .15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.65'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>Forgot password?</Link>
              </div>
              <input
                type="password" required placeholder="••••••••"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '11px 16px', color: '#fff', fontSize: 13.5, outline: 'none', transition: 'border-color .15s' }}
                onFocus={e => e.target.style.borderColor = GA(0.4)}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              />
            </div>

            {/* Remember me */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, rememberMe: !formData.rememberMe })}
              style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 2, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <div
                style={{
                  flexShrink: 0, width: 16, height: 16, borderRadius: 4,
                  background: formData.rememberMe ? 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.38) 0%, rgba(200,160,78,0.18) 55%, rgba(200,160,78,0.08) 100%)' : 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                  border: formData.rememberMe ? '1px solid rgba(200,160,78,0.45)' : '1px solid rgba(255,255,255,0.12)',
                  boxShadow: formData.rememberMe ? '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(200,160,78,0.3)' : '0 2px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.07)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all .15s',
                }}
              >
                {formData.rememberMe && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5L4.5 7.5L8 2.5" stroke="#c8a04e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span style={{ fontSize: 12, color: W(0.35) }}>Remember this device</span>
            </button>

            <motion.button
              whileTap={{ scale: 0.98 }}
              disabled={loading}
              style={{ marginTop: 4, padding: '13px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)', border: `1px solid ${GA(0.28)}`, boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(200,160,78,0.1)', color: GOLD, fontWeight: 700, fontSize: 13.5, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, letterSpacing: '0.01em', transition: 'background .2s, border-color .2s' }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.18) 0%, rgba(200,160,78,0.08) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.45)'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = GA(0.28); }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </motion.button>
          </form>

          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: W(0.35), margin: 0 }}>
              Don't have an account?{' '}
              <Link to="/register" style={{ color: GOLD, fontWeight: 600, textDecoration: 'none', transition: 'opacity .15s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.65'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>Create One</Link>
            </p>
          </div>
        </motion.div>

        {/* Back to home */}
        <Link to="/" style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', fontSize: 12, color: W(0.25), textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, transition: 'color .15s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.color = W(0.6)}
          onMouseLeave={e => e.currentTarget.style.color = W(0.25)}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Back to Homepage
        </Link>
      </div>
    </div>
  );
}
